import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface LeaveRequest {
  id: string
  employee_name: string
  leave_type: string
  start_date: string
  end_date: string
  days_requested: number
  status: string
  reason: string
}

interface LeaveBalance {
  id: string
  employee_name: string
  annual_remaining: number
  sick_remaining: number
  personal_remaining: number
}

export default function LeavePage() {
  const [requests, setRequests] = useState<LeaveRequest[]>([])
  const [balances, setBalances] = useState<LeaveBalance[]>([])
  const [statistics, setStatistics] = useState<any>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [reqRes, balRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/leave/requests'),
      fetch(API_BASE + '/leave/balances'),
      fetch(API_BASE + '/leave/statistics')
    ])
    if (reqRes.ok) setRequests((await reqRes.json()).leave_requests || [])
    if (balRes.ok) setBalances((await balRes.json()).leave_balances || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-800'
      case 'pending': return 'bg-yellow-100 text-yellow-800'
      case 'rejected': return 'bg-red-100 text-red-800'
      case 'cancelled': return 'bg-gray-100 text-gray-600'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getLeaveTypeColor = (type: string) => {
    switch (type) {
      case 'annual': return 'text-blue-600'
      case 'sick': return 'text-red-600'
      case 'personal': return 'text-purple-600'
      default: return 'text-gray-600'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Leave Management</h1>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ Request Leave</button>
        </div>

        {statistics && (
          <div className="grid grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Requests</div>
              <div className="text-2xl font-bold">{statistics.total_requests}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Pending</div>
              <div className="text-2xl font-bold text-yellow-600">{statistics.pending_requests}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">On Leave Today</div>
              <div className="text-2xl font-bold text-blue-600">{statistics.employees_on_leave_today}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Annual Utilization</div>
              <div className="text-2xl font-bold">{statistics.annual_leave_utilization}%</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Upcoming 7 Days</div>
              <div className="text-2xl font-bold text-green-600">{statistics.upcoming_leaves_7_days}</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Leave Requests</h2></div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dates</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {requests.slice(0, 12).map(req => (
                  <tr key={req.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium">{req.employee_name}</div>
                      <div className="text-xs text-gray-500">{req.reason}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`text-sm font-medium capitalize ${getLeaveTypeColor(req.leave_type)}`}>
                        {req.leave_type.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">{req.start_date} - {req.end_date}</div>
                      <div className="text-xs text-gray-500">{req.days_requested} days</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs ${getStatusColor(req.status)}`}>{req.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Leave Balances</h3>
            <div className="space-y-3">
              {balances.slice(0, 10).map(bal => (
                <div key={bal.id} className="p-3 bg-gray-50 rounded">
                  <div className="font-medium text-sm mb-2">{bal.employee_name}</div>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div className="text-center">
                      <div className="text-blue-600 font-bold">{bal.annual_remaining}</div>
                      <div className="text-gray-500">Annual</div>
                    </div>
                    <div className="text-center">
                      <div className="text-red-600 font-bold">{bal.sick_remaining}</div>
                      <div className="text-gray-500">Sick</div>
                    </div>
                    <div className="text-center">
                      <div className="text-purple-600 font-bold">{bal.personal_remaining}</div>
                      <div className="text-gray-500">Personal</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
